import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { LearningPanel } from "@/components/LearningPanel";
import { StrategiesPanel, AutotunePanel } from "@/components/StrategiesPanel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REASONS = {
  mtf: "Higher-TF trend against",
  corr: "BTC correlation too high",
  funding: "Funding too expensive",
  no_signal: "No entry setup",
  no_data: "Not enough candle data",
  priority: "Not a priority strategy",
  confluence: "Candle + ML confluence too weak",
  imbalance: "Orderbook imbalance veto",
  score: "Quality score below min",
  rank_cap: "Outranked (position cap)",
  learned_block: "Blocked by learning (past losses)",
};

const fmtTime = (v) => {
  if (!v) return "—";
  const d = new Date(typeof v === "number" ? (v < 1e12 ? v * 1000 : v) : v);
  return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
};

const ScoreBar = ({ value, threshold, testid }) => {
  const pct = Math.max(0, Math.min(1, value ?? 0)) * 100;
  const ok = (value ?? 0) >= threshold;
  return (
    <div className="flex items-center gap-1.5 min-w-0" data-testid={testid}>
      <div className="h-1.5 flex-1 bg-slate-800 relative overflow-hidden">
        <div className={`h-full ${ok ? "bg-emerald-500/70" : "bg-slate-500/60"}`} style={{ width: `${pct}%` }} />
        <div className="absolute top-0 h-full w-px bg-amber-400" style={{ left: `${threshold * 100}%` }} />
      </div>
      <span className={`font-mono tabular-nums w-9 text-right ${ok ? "text-emerald-300" : "text-slate-400"}`}>
        {value == null ? "—" : value.toFixed(2)}
      </span>
    </div>
  );
};

const DecisionRow = ({ d, gate }) => {
  const sym = d.symbol.replace("-USDT-SWAP", "");
  const taken = d.decision === "QUALIFIED";
  const patDir = d.side === "LONG" ? d.bull_raw : d.side === "SHORT" ? d.bear_raw : null;
  return (
    <div
      className={`border-b border-slate-800/60 px-2 py-2 transition-colors hover:bg-slate-800/40 ${taken ? "bg-emerald-950/20" : ""}`}
      data-testid={`gate-row-${sym}`}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <span className="font-mono text-slate-100 truncate">{sym}</span>
          {d.side ? (
            <span className={`label-uppercase text-[9px] px-1 border ${d.side === "LONG" ? "text-emerald-300 border-emerald-600/50" : "text-red-300 border-red-600/50"}`}>{d.side}</span>
          ) : null}
          {d.strategy ? <span className="text-slate-500 text-[10px] truncate">{d.strategy}</span> : null}
        </div>
        <span
          className={`label-uppercase text-[9px] px-1.5 py-0.5 border shrink-0 ${taken ? "text-emerald-300 border-emerald-500/60" : "text-slate-400 border-slate-600"}`}
          data-testid={`gate-decision-${sym}`}
        >
          {taken ? "QUALIFIED" : "SKIPPED"}
        </span>
      </div>
      <div className="grid grid-cols-3 gap-2 mt-1.5 text-[10px]">
        <div>
          <div className="text-slate-500 label-uppercase text-[9px]">Candle {d.pattern_ok ? "✓" : ""}</div>
          <span className={`font-mono ${d.pattern_ok ? "text-emerald-300" : "text-slate-300"}`} data-testid={`gate-candle-${sym}`}>
            {patDir != null
              ? <>{patDir.toFixed(2)} <span className="text-slate-600">/ {gate.hard_entry_pattern_score}</span></>
              : d.bull_raw != null ? <>↑{d.bull_raw.toFixed(1)} ↓{d.bear_raw.toFixed(1)}</> : "—"}
          </span>
        </div>
        <div>
          <div className="text-slate-500 label-uppercase text-[9px]">ML {d.ml_ok ? "✓" : ""}</div>
          <span className={`font-mono ${d.ml_ok ? "text-emerald-300" : "text-slate-300"}`} data-testid={`gate-ml-${sym}`}>
            {d.ml_prob != null ? `${(d.ml_prob * 100).toFixed(1)}%` : d.side ? "no model" : "—"}
          </span>
        </div>
        <div>
          <div className="text-slate-500 label-uppercase text-[9px]">Confluence</div>
          <ScoreBar value={d.confluence} threshold={gate.min_confluence_score ?? 0.6} testid={`gate-conf-${sym}`} />
        </div>
      </div>
      {!taken && d.reason ? (
        <div className="text-amber-300/80 text-[10px] mt-1" data-testid={`gate-reason-${sym}`}>
          {REASONS[d.reason] || d.reason}{d.learn_note ? ` — ${d.learn_note}` : ""}
        </div>
      ) : null}
      {d.learn_adj ? (
        <div className={`text-[10px] mt-0.5 ${d.learn_adj > 0 ? "text-emerald-400/80" : "text-red-400/80"}`} data-testid={`gate-learn-${sym}`}>
          Learning {d.learn_adj > 0 ? "+" : ""}{d.learn_adj} pts{d.learn_p_win != null ? ` · P(win) ${(d.learn_p_win * 100).toFixed(0)}%` : ""}
        </div>
      ) : null}
    </div>
  );
};

const RetrainCard = ({ sched, ml, history, busy, onTrain }) => {
  const last = history[0];
  return (
    <div className="border border-slate-700/60 bg-slate-900/40 p-3" data-testid="ml-retrain-card">
      <div className="flex items-center justify-between mb-2">
        <span className="label-uppercase text-cyan-300">Daily ML Retrain</span>
        <span className={`label-uppercase text-[10px] px-2 py-0.5 border ${ml?.trained ? "text-emerald-400 border-emerald-500/60" : "text-amber-400 border-amber-500/60"}`} data-testid="ml-model-status">
          {ml?.trained ? "MODEL LOADED" : "NO MODEL"}
        </span>
      </div>
      <div className="text-slate-500 text-[10px] mb-2" data-testid="ml-schedule-text">{sched?.schedule || "—"}</div>
      <div className="grid grid-cols-2 gap-1 text-[10px] mb-2">
        <Stat label="Last trained" value={fmtTime(ml?.trained_at)} testid="ml-trained-at" />
        <Stat label="Next run" value={fmtTime(sched?.next_run_utc)} testid="ml-next-run" />
        <Stat label="Test AUC" value={ml?.metrics?.test_auc ?? "—"} testid="ml-test-auc" />
        <Stat label="Accuracy" value={ml?.metrics?.test_accuracy ?? "—"} testid="ml-test-acc" />
      </div>
      {last ? (
        <div className="text-[10px] text-slate-400 mb-2" data-testid="ml-last-run">
          Last run {fmtTime(last.ts_utc)} ·{" "}
          {last.replaced === false
            ? <span className="text-amber-300">kept current model (candidate AUC {last.candidate_auc} &lt; {last.current_auc})</span>
            : last.ok
              ? <span className="text-emerald-300">new model deployed{last.candidate_auc != null ? ` (AUC ${last.candidate_auc})` : ""}</span>
              : <span className="text-red-300">failed{last.error ? ` — ${last.error}` : ""}</span>}
        </div>
      ) : null}
      <button
        onClick={onTrain}
        disabled={busy}
        className="w-full border border-cyan-500/40 text-cyan-300 label-uppercase px-2 py-1.5 transition-colors hover:bg-cyan-950/40 disabled:opacity-50"
        data-testid="btn-ml-retrain-now"
      >
        {busy ? "Starting…" : "Retrain Now"}
      </button>
    </div>
  );
};

const Stat = ({ label, value, testid }) => (
  <div className="border border-slate-800 p-1.5">
    <div className="text-slate-500 label-uppercase text-[9px]">{label}</div>
    <div className="font-mono text-slate-100 truncate" data-testid={testid}>{value}</div>
  </div>
);

export const GatePanel = () => {
  const [gate, setGate] = useState(null);
  const [sched, setSched] = useState(null);
  const [ml, setMl] = useState(null);
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState("ALL");
  const [scanning, setScanning] = useState(false);

  const load = async () => {
    try {
      const [g, s, m, h] = await Promise.all([
        axios.get(`${API}/gate/decisions`),
        axios.get(`${API}/ml/schedule`),
        axios.get(`${API}/ml/status`),
        axios.get(`${API}/ml/history?limit=1`),
      ]);
      setGate(g.data);
      setSched(s.data);
      setMl(m.data);
      setHistory(h.data?.runs || []);
    } catch (e) {
      // keep last snapshot
    }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 8000);
    return () => clearInterval(id);
  }, []);

  const trainNow = async () => {
    setBusy(true);
    try {
      const res = await axios.post(`${API}/ml/schedule/train-now`);
      toast.success(res.data?.message || "Retrain started");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Retrain failed to start");
    } finally {
      setBusy(false);
    }
  };

  const scanNow = async () => {
    setScanning(true);
    try {
      const res = await axios.post(`${API}/gate/scan-now`);
      toast.success(`Scan done — ${res.data?.qualified ?? 0} qualified`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Scan failed");
    } finally {
      setScanning(false);
    }
  };

  const decisions = (gate?.decisions || []).filter((d) => filter === "ALL" || d.decision === filter);

  return (
    <div className="h-full overflow-y-auto overflow-x-hidden text-[11px] md:text-xs text-slate-300 p-3 space-y-4" data-testid="gate-panel">
      <RetrainCard sched={sched} ml={ml} history={history} busy={busy} onTrain={trainNow} />
      <StrategiesPanel />
      <AutotunePanel />
      <LearningPanel />

      <div className="border border-slate-700/60 bg-slate-900/40">
        <div className="p-3 pb-2 border-b border-slate-800">
          <div className="flex items-center justify-between mb-1">
            <span className="label-uppercase text-cyan-300">Gate Decisions</span>
            <span className="font-mono text-slate-400 text-[10px]" data-testid="gate-scan-time">{fmtTime(gate?.ts)}</span>
          </div>
          <div className="text-[10px] text-slate-500" data-testid="gate-summary">
            {gate?.evaluated ?? 0} scanned · {gate?.with_signal ?? 0} with signal ·{" "}
            <span className="text-emerald-300">{gate?.qualified ?? 0} qualified</span>
            {gate && !gate.gate_enabled ? <span className="text-amber-300"> · gate OFF</span> : null}
          </div>
          <div className="text-[10px] text-slate-600 mt-0.5">
            Needs candle ≥ {gate?.hard_entry_pattern_score ?? 1.5} or ML ≥ {((gate?.hard_entry_ml_prob ?? 0.6) * 100).toFixed(0)}%, confluence ≥ {(gate?.min_confluence_score ?? 0.6).toFixed(2)}
            {gate?.min_entry_score != null ? `, score ≥ ${gate.min_entry_score}` : ""} (learning-adjusted)
          </div>
          <div className="flex gap-1 mt-2 items-center">
            {["ALL", "QUALIFIED", "SKIPPED"].map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`whitespace-nowrap label-uppercase text-[9px] px-1.5 py-0.5 border transition-colors ${filter === f ? "border-cyan-400 text-cyan-300 bg-cyan-950/30" : "border-slate-700 text-slate-500 hover:text-slate-300"}`}
                data-testid={`gate-filter-${f.toLowerCase()}`}
              >
                {f}
              </button>
            ))}
            <button
              onClick={scanNow}
              disabled={scanning}
              className="ml-auto whitespace-nowrap shrink-0 label-uppercase text-[9px] px-2 py-0.5 border border-emerald-500/50 text-emerald-300 transition-colors hover:bg-emerald-950/40 disabled:opacity-50"
              data-testid="gate-scan-now"
            >
              {scanning ? "Scanning…" : "Scan Now"}
            </button>
          </div>
        </div>
        <div className="max-h-[480px] overflow-y-auto" data-testid="gate-decisions-list">
          {decisions.length === 0 ? (
            <div className="text-slate-500 label-uppercase text-[10px] p-3" data-testid="gate-empty">
              {!gate?.ts ? "Awaiting first scan — start the bot or tap Scan Now"
                : gate.with_signal === 0 ? `No entry signals this scan (${gate.evaluated} coins scanned)` : "No coins in this view"}
            </div>
          ) : decisions.map((d) => <DecisionRow key={d.symbol} d={d} gate={gate} />)}
        </div>
      </div>
    </div>
  );
};
